import type { Translate } from 'next-translate';
import Image from 'next/image';
import React from 'react';
import MarqueeLib from 'react-fast-marquee';
import { TrustpilotWidget } from '@/components/elements/TrustpilotWidget';
import { useIsTablet } from '@/hooks';
import type { LandingMain } from '@/types';
import gradient from '@/public/images/landing/home/testimonials/gradient.webp';
import stars from '@/public/images/landing/home/testimonials/stars.webp';
import styles from './MainTestimonials.module.scss';

interface Props {
	data: LandingMain['testimonials'];
	t: Translate;
	locale: string;
}

// UTC so the server and the browser print the same day
const formatDate = (date: string, locale: string) => {
	try {
		return new Intl.DateTimeFormat(locale, {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			timeZone: 'UTC',
		}).format(new Date(date));
	} catch {
		return date;
	}
};

export const MainTestimonials: React.FC<Props> = ({ data, t, locale }) => {
	const isTablet = useIsTablet();

	const cards = (
		<ul className={styles['testimonials__list']}>
			{data.list.map(review => (
				<li key={review.id} className={styles['card']}>
					{/* The whole card opens the review on Trustpilot */}
					<a
						href={review.link}
						target='_blank'
						rel='noopener noreferrer'
						className={styles['card__box']}
					>
						<span className={styles['card__header']}>
							<span className={styles['card__author']}>{review.author}</span>
							<span className={styles['card__link']}>{t(data.linkText)}</span>
						</span>
						<time className={styles['card__date']} dateTime={review.date}>
							{formatDate(review.date, locale)}
						</time>
						<Image
							src={stars}
							alt={t(data.ratingLabel)}
							width={85}
							height={16}
							className={styles['card__stars']}
						/>
						<span className={styles['card__title']}>{review.title}</span>
						<span className={styles['card__text']}>{review.text}</span>
					</a>
				</li>
			))}
		</ul>
	);

	return (
		<section className={styles['testimonials']}>
			<Image
				src={gradient}
				alt=''
				fill
				sizes='100vw'
				className={styles['testimonials__background']}
				aria-hidden='true'
			/>

			<h2 className={styles['testimonials__title']}>{t(data.title)}</h2>

			{/* Same running ribbon as the previous landing; phones scroll it by hand */}
			<div className={styles['testimonials__ribbon']}>
				{isTablet ? (
					cards
				) : (
					<MarqueeLib pauseOnHover speed={40}>
						{cards}
					</MarqueeLib>
				)}
			</div>

			<div className={styles['testimonials__rating']}>
				<TrustpilotWidget locale={locale} height='24px' />
			</div>
		</section>
	);
};
